import { useCallback, useMemo, useState } from 'react'

// Every view ReservationCalendar can render.
export const CALENDAR_VIEWS = ['simple', 'horizontal', 'vertical', 'lastminute', 'flight']

// Views a user may choose. 'flight' is a capability constraint of the venue, never
// a preference, so it is deliberately absent. 'multi' is absent because
// SimpleMultiView was never wired up (see INIT_DOC.md "Known issues").
export const SELECTABLE_VIEWS = ['simple', 'horizontal', 'vertical', 'lastminute']

// Views offerable as profile.defaultview / appconfig.forceview.
export const DEFAULTABLE_VIEWS = ['simple', 'horizontal', 'vertical']

export const FALLBACK_VIEW = 'simple'

// sessionStorage is per-origin, but on utest/localhost one origin serves many
// tenants (App.js picks the init doc from the first path segment), so the key
// has to carry the tenant or a pick at venue A would follow you to venue B.
const sessionKey = (appconfig) =>
  'kumpula.calendarview.' + ((appconfig && appconfig.accountkey) || 'default')

const isSelectable = (v) => typeof v === 'string' && SELECTABLE_VIEWS.includes(v)

const readSessionChoice = (appconfig) => {
  try {
    const v = window.sessionStorage.getItem(sessionKey(appconfig))
    return isSelectable(v) ? v : null
  } catch (e) {
    return null // Safari private mode / storage disabled
  }
}

const writeSessionChoice = (appconfig, v) => {
  try {
    if (isSelectable(v)) {
      window.sessionStorage.setItem(sessionKey(appconfig), v)
    } else {
      window.sessionStorage.removeItem(sessionKey(appconfig))
    }
  } catch (e) {
    // storage unavailable - the choice just won't survive a remount
  }
}

// Exported so ProfileScreen can drop a stale session pick after the user saves a
// new default, without pulling in the whole hook.
export const clearCalendarViewChoice = (appconfig) => writeSessionChoice(appconfig, null)

/**
 * Single source of truth for which calendar view is on screen.
 *
 * Precedence, highest first:
 *   1. appconfig.flightview   tenant-level lock, switcher hidden
 *   2. category.flightview    capability constraint of the selected venue
 *   3. session choice         sessionStorage: survives an SPA remount and F5, dies with the tab
 *   4. profile.defaultview    the user's saved preference
 *   5. appconfig.forceview    tenant starting view (INIT_DOC: beaten by defaultview)
 *   6. 'simple'
 *
 * 'lastminute' only means anything on the current day, so when
 * lastMinuteAvailable is false a stored 'lastminute' is skipped and resolution
 * continues down the list. Without that, picking it and then paging to another
 * day would strand the user on an empty NowAvailable with no chip highlighted.
 *
 * viewStyle is DERIVED - nothing here writes state during render.
 */
export default function useCalendarView({
  appconfig,
  profile,
  selectedCategory,
  lastMinuteAvailable = true,
}) {
  // State is a cache of sessionStorage, seeded lazily on mount. Remounting the
  // screen re-reads storage, which is what makes a manual pick survive
  // navigating away to /reservation and back.
  const [sessionChoice, setSessionChoice] = useState(() => readSessionChoice(appconfig))

  const constraint = useMemo(() => {
    if (!appconfig) return null
    if (appconfig.flightview) return 'flight'
    const cat = (appconfig.categories || []).find((c) => c.category === selectedCategory)
    return cat && cat.flightview ? 'flight' : null
  }, [appconfig, selectedCategory])

  const viewStyle = useMemo(() => {
    if (constraint) return constraint
    const candidates = [
      sessionChoice,
      profile && profile.defaultview,
      appconfig && appconfig.forceview,
    ]
    for (const c of candidates) {
      if (!isSelectable(c)) continue
      if (c === 'lastminute' && !lastMinuteAvailable) continue
      return c
    }
    return FALLBACK_VIEW
  }, [constraint, sessionChoice, profile, appconfig, lastMinuteAvailable])

  // Called only from event handlers. Silently ignores 'flight', 'multi' and junk.
  const setView = useCallback(
    (view) => {
      if (!isSelectable(view)) return
      writeSessionChoice(appconfig, view)
      setSessionChoice(view)
    },
    [appconfig]
  )

  const resetView = useCallback(() => {
    writeSessionChoice(appconfig, null)
    setSessionChoice(null)
  }, [appconfig])

  return {
    viewStyle,
    setView,
    resetView,
    // The tenant pins the view -> hide the switcher entirely.
    canSwitchView: !constraint,
  }
}
